import {
  getAlertsService,
  resolveAlertService,
  getFreezesService,
  createFreezeService,
  resolveFreezeService,
  getSosReportsService,
  createSosReportService,
  updateSosReportService,
} from "@/services/compliance.service";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const ALERTS_KEY = ["alerts"] as const;
const FREEZES_KEY = ["freezes"] as const;
const SOS_KEY = ["sos-reports"] as const;

// ─── Pattern Alerts ───

interface AlertFilters {
  page?: number;
  limit?: number;
  status?: string;
  riskLevel?: string;
  patternType?: string;
  userId?: string;
}

export const useAlerts = (filters: AlertFilters = {}) =>
  useQuery({
    queryKey: [...ALERTS_KEY, filters],
    queryFn: async () => {
      const {data} = await getAlertsService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useResolveAlert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, body}: {id: string; body: {status: string; resolution?: string; resolutionNotes?: string}}) =>
      resolveAlertService(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ALERTS_KEY});
    },
  });
};

// ─── Account Freezes ───

interface FreezeFilters {
  page?: number;
  limit?: number;
  userId?: string;
  freezeType?: string;
  isActive?: boolean;
}

export const useFreezes = (filters: FreezeFilters = {}) =>
  useQuery({
    queryKey: [...FREEZES_KEY, filters],
    queryFn: async () => {
      const {data} = await getFreezesService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useCreateFreeze = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof createFreezeService>[0]) => createFreezeService(body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: FREEZES_KEY});
    },
  });
};

export const useResolveFreeze = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, body}: {id: string; body: {resolution: string; fundsStatus?: string; notes?: string}}) =>
      resolveFreezeService(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: FREEZES_KEY});
    },
  });
};

// ─── SOS Reports ───

interface SosFilters {
  page?: number;
  limit?: number;
  userId?: string;
  status?: string;
}

export const useSosReports = (filters: SosFilters = {}) =>
  useQuery({
    queryKey: [...SOS_KEY, filters],
    queryFn: async () => {
      const {data} = await getSosReportsService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useCreateSosReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof createSosReportService>[0]) => createSosReportService(body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: SOS_KEY});
    },
  });
};

export const useUpdateSosReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, body}: {id: string; body: Parameters<typeof updateSosReportService>[1]}) =>
      updateSosReportService(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: SOS_KEY});
    },
  });
};
